import { type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import fassychanGif from '../../../assets/fassychan.gif';

/** @graph-connects none */
export interface FassychanLoadingOverlayProps {
  /** 中央に表示するステータス文言（例: 「起票処理中...」） */
  label: string;
  /**
   * document.body へ portal するか。開いている `Modal`（`z-[var(--z-modal)]`）より前面へ
   * 出す必要がある場合に true。既定 false（呼び出し元のツリー内にそのまま描画）。
   */
  portal?: boolean;
  /**
   * オーバーレイの z-index を指定する Tailwind クラス。既定 'z-50'。
   * 開いている Modal より前面に出す場合は 'z-[calc(var(--z-modal)+1)]' 等を渡す。
   */
  zIndexClassName?: string;
}

/**
 * fassychan GIF の全画面ローディングオーバーレイ（純表示コンポーネント）。
 * 待機中の処理（起票・セルフアサイン等）に共通で使う。z-index / portal / 文言のみ
 * props で切り替え、GIF・レイアウト・配色は 1 か所に集約して両呼び出し元の挙動ドリフトを防ぐ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business fassychan GIF を中央に据えた全画面ローディング表示。起票中・セルフアサイン中などの待機画面で共有利用する
 * @graph-connects none
 */
export function FassychanLoadingOverlay({
  label,
  portal = false,
  zIndexClassName = 'z-50',
}: FassychanLoadingOverlayProps): ReactNode {
  const overlay = (
    <div
      className={`fixed inset-0 ${zIndexClassName} flex flex-col items-center justify-center gap-3 bg-[var(--color-bg-base)]/80 backdrop-blur-sm`}
      role="status"
      aria-live="polite"
    >
      <img src={fassychanGif} alt="" aria-hidden="true" className="h-40 w-40 object-contain" />
      <span className="text-sm text-[var(--color-text-secondary)]">{label}</span>
    </div>
  );
  return portal ? createPortal(overlay, document.body) : overlay;
}
